import Cookies from "js-cookie";

/**
 * Name of the CSRF cookie the API itself sets. On deployments where the app and
 * the API live on different hosts of the same site (app.caltana.com vs
 * app-api.caltana.com) this cookie is domain-scoped to `.caltana.com`, and it is
 * the ONLY CSRF cookie the API receives and validates against.
 */
const CSRF_COOKIE_NAME = "csrftoken";

/**
 * Name of the CSRF token web-next stores for itself from the login response.
 *
 * It is deliberately NOT `csrftoken`. `document.cookie` exposes neither domain
 * nor path, so {@link getCookieValue} can only return the first cookie with a
 * given name — and a client-written `csrftoken` on the app host is the older of
 * the two entries (browsers keep a cookie's original creation time when its
 * value is updated), so it wins that race permanently and shadows the API's
 * cookie. Sending that shadow in `X-CSRFToken` is what made logout fail with
 * `403 CSRF Failed` on the first attempt after every login.
 *
 * Under its own name it can only ever be a fallback, for split-site deployments
 * (Vercel previews) where the API's cookie is not readable from this origin.
 */
export const CLIENT_CSRF_COOKIE_NAME = "caltana_client_csrftoken";

const getCookieValue = (cookieName: string) => {
  if (typeof window === "undefined" || !document.cookie) {
    return "";
  }

  const cookies = document.cookie.split(";");
  for (const cookie of cookies) {
    const parts = cookie.split("=");
    if (parts.length >= 2) {
      const name = parts[0].trim();
      if (name === cookieName) {
        // Join the rest of the parts in case the value itself contained '='
        const value = parts.slice(1).join("=").trim();
        return value;
      }
    }
  }

  return "";
};

/**
 * The CSRF token to send in `X-CSRFToken`.
 *
 * Always prefers the API's own cookie, because that is the value the server
 * compares against. The client-stored copy is only used when the API's cookie is
 * invisible to this origin.
 */
export const getCsrfToken = () =>
  getCookieValue(CSRF_COOKIE_NAME) || getCookieValue(CLIENT_CSRF_COOKIE_NAME);

export const getAuthToken = () => getCookieValue("auth_token");

/**
 * Whether the API answers on a different host than the app.
 *
 * On a split-host deployment (app.caltana.com vs app-api.caltana.com, or a
 * Vercel preview against dev-api.caltana.com) the API's own `csrftoken` is a
 * domain cookie and never host-only for this origin — so a host-only
 * `csrftoken` here can only be a copy this app wrote, and is safe to drop.
 *
 * When the two share a host (local development) there is exactly one
 * `csrftoken` cookie and it belongs to the API. Deleting it would leave the
 * server with nothing to validate against, so the migration below leaves it
 * alone.
 */
export function isApiOnAnotherHost(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  const apiUrl = process.env.NEXT_PUBLIC_API_URL;
  if (!apiUrl) {
    return false;
  }

  try {
    return new URL(apiUrl, window.location.origin).hostname !== window.location.hostname;
  } catch {
    // An unparseable API URL tells us nothing, so take the branch that never
    // deletes a cookie.
    return false;
  }
}

/**
 * Retires the host-only `csrftoken` copy written by builds that predate
 * {@link CLIENT_CSRF_COOKIE_NAME}.
 *
 * Renaming the copy stops new shadows, but a browser carrying an old one keeps
 * it — and it still wins {@link getCsrfToken}, so CSRF-protected mutations go on
 * failing until it is gone. Sessions that never sign in again would keep it
 * indefinitely, so this runs at app boot as well as at login.
 *
 * The value is carried over to the client cookie first, because on a
 * cross-site host (a Vercel preview) that copy is the only CSRF token readable
 * from this origin.
 */
export function retireLegacyCsrfCookie(): void {
  if (!isApiOnAnotherHost()) {
    return;
  }

  const legacyToken = getCookieValue(CSRF_COOKIE_NAME);
  if (!legacyToken) {
    return;
  }

  if (!getCookieValue(CLIENT_CSRF_COOKIE_NAME)) {
    Cookies.set(CLIENT_CSRF_COOKIE_NAME, legacyToken, getClientCookieOptions());
  }

  // Without a domain, js-cookie can only expire the host-only cookie; the API's
  // domain-scoped one is untouched.
  Cookies.remove(CSRF_COOKIE_NAME, { secure: true });
}

/**
 * Attributes for the cookies this app writes itself. Shared with the login form
 * so a migrated CSRF value and a freshly issued one land on the same cookie
 * rather than two.
 */
export function getClientCookieOptions(): { sameSite: "strict" | "lax"; secure?: true } {
  const isSecure = window.location.protocol === "https:";
  const isProduction = window.location.hostname.endsWith(".caltana.com");

  if (isSecure || isProduction) {
    return { sameSite: "strict", secure: true };
  }

  return { sameSite: "lax" };
}
